import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from './Hoverable';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';

interface SectionHeaderProps {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Tightened rhythm for pages that are sized to fit rather than to scroll.
   * The default 20/20 is generous on purpose — it separates stacked sections on
   * a scrolling page — but on the no-scroll home four of those headers spend
   * ~160pt of a 844pt phone on whitespace, which comes straight out of the
   * planner. `dense` trades that for 8 above / 4 below: enough to read as a
   * break between sections, and the mock's actual spacing.
   *
   * Off by default, so every existing caller keeps the exact padding it has.
   */
  dense?: boolean;
}

export const SectionHeader: React.FC<SectionHeaderProps> = React.memo(({
  title,
  actionLabel,
  onAction,
  dense = false,
}) => {
  const { colors, semantic } = useThemeColors();
  const type = useTypography();
  const space = useSpacing();

  // Scale-local tokens, not literals: xl(20) is today's padding exactly, and
  // dense is sm(8)/xs(4). Asymmetric on purpose — the header reads as a lid on
  // the card below it, so it keeps its air above and gives up the air below.
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: dense ? space.sm : space.xl,
          paddingBottom: dense ? space.xs : space.xl,
        },
      }),
    [dense, space],
  );

  return (
    <View style={styles.container}>
      <Text style={[type.sectionTitle, { color: colors.text.secondary }]}>{title}</Text>
      {actionLabel && onAction && (
        <Hoverable hover="link" onPress={onAction} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          {({ textStyle }) => (
            /* semantic.text.link, not the raw blue primitive it used to hardcode:
               the primitive is tuned as a fill for light surfaces and is far
               under AA as ink on the dark page background this header also sits
               on. The token carries the per-theme value. */
            <Text style={[type.small, { color: semantic.text.link }, textStyle]}>{actionLabel}</Text>
          )}
        </Hoverable>
      )}
    </View>
  );
});

SectionHeader.displayName = 'SectionHeader';
